import axios from "@/libs/axios";
import { useChatsStore } from "@/store/useChatsStore";
import { useTeamsStore } from "@/store/useTeamsStore";
import { useUsersStore } from "@/store/useUsersStore";
import { ChatChannel } from "@/views/modules/messaging-inbox/useChat";
import { RequestQueryBuilder } from "@rewiko/crud-request";
import { useQuery } from "@tanstack/vue-query";
import { computed } from "@vue/composition-api";
import { AxiosResponse } from "axios";
import { defineStore, storeToRefs } from "pinia";

type ChannelsResponse = Omit<ChatChannel, "teamChannels" | "userChannels"> & {
    teamChannels: Array<{
        id: number;
        teamId: number;
    }>;
    userChannels: Array<{
        id: number;
        userId: number;
    }>;
};

interface State {
    isLoadingChannels: boolean;
    channels: ChatChannel[];
}

export const useChannelsStore = defineStore("channels", {
    state: (): State => ({
        isLoadingChannels: false,
        channels: [],
    }),
    actions: {
        fetchChannels(requestQueryBuilder?: RequestQueryBuilder, forceRefetch = false): void {
            const usersStore = useUsersStore();
            const teamsStore = useTeamsStore();

            const { users } = storeToRefs(usersStore);
            const { teams } = storeToRefs(teamsStore);

            const isUsersTeamsLoaded = computed(() => !!users.value && !!teams.value);

            useQuery<Pick<AxiosResponse<ChannelsResponse[]>, "data">>(
                ["channels", requestQueryBuilder?.queryObject, forceRefetch],
                () => {
                    this.isLoadingChannels = true;

                    return axios.get<ChannelsResponse[]>(`${process.env.VUE_APP_PUBLIC_API}/channels`, {
                        params: requestQueryBuilder?.queryObject,
                    });
                },
                {
                    staleTime: forceRefetch ? 0 : 300000, // 5min
                    placeholderData: { data: [] },
                    onSuccess: (response) =>
                        (this.channels = response.data.map(
                            (channel) =>
                                ({
                                    id: channel.id,
                                    platform: channel.platform,
                                    teamChannels: channel.teamChannels
                                        .map((teamChannel) =>
                                            teams.value.find((team) => team.id === teamChannel.teamId)
                                        )
                                        .filter(Boolean),
                                    userChannels: channel.userChannels
                                        .map((userChannel) =>
                                            users.value.find((user) => user.id === userChannel.userId)
                                        )
                                        .filter(Boolean),
                                    name: channel.name,
                                    customerCareWindowLength: channel.customerCareWindowLength,
                                    settings: channel.settings ?? [],
                                    provider: channel.provider,
                                } as ChatChannel)
                        )),
                    onSettled: () => (this.isLoadingChannels = false),
                    enabled: isUsersTeamsLoaded, // This will ensure that the request has been executed when the data of these requests are finished @see https://tanstack.com/query/latest/docs/framework/vue/guides/dependent-queries
                }
            );
        },
    },
    getters: {
        activeChatChannel(state): ChatChannel {
            const chatsStore = useChatsStore();
            return state.channels.find((channel) => channel.id === chatsStore.activeChat?.chat.channelId);
        },
        getChannelById(state): (channelId: number) => ChatChannel {
            return (channelId: number) => state.channels.find((channel) => channel.id === channelId);
        },
    },
});
